import { jurionChatService } from "./jurionChatService";
import { RedlineSuggestion } from "../types";

const REDLINE_PROMPT = `You are a senior legal editor reviewing the supplied document text. Identify concrete improvements and return them as a JSON code block ONLY (no prose before or after).

Format strictly:
\`\`\`json
{
  "suggestions": [
    {
      "title": "Short label of the change (5-8 words)",
      "originalText": "Exact verbatim text from the document to be replaced. Must match character-for-character so it can be found and replaced.",
      "suggestedText": "Revised text that should replace it.",
      "reason": "1-2 sentence explanation of why this change improves the clause."
    }
  ]
}
\`\`\`

Rules:
- "originalText" MUST be a verbatim substring of the supplied text. Do not paraphrase or normalise punctuation.
- Keep each suggestion focused on one specific edit. Do not bundle several edits into one item.
- 3-10 suggestions per request is ideal. If the text is very short, fewer is fine.
- If you cannot find any improvements, return an empty array.
- Output ONLY the JSON block. No preamble, no closing remarks.`;

const stripFence = (text: string): string => {
  const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenceMatch) {
    return fenceMatch[1].trim();
  }
  return text.trim();
};

const tryParseJson = (text: string): any => {
  try {
    return JSON.parse(text);
  } catch {
    // Attempt to extract a {...} object from the text
    const objectMatch = text.match(/\{[\s\S]*\}/);
    if (objectMatch) {
      try {
        return JSON.parse(objectMatch[0]);
      } catch {
        return null;
      }
    }
    return null;
  }
};

const normalize = (raw: any, sourceText: string): RedlineSuggestion[] => {
  const items: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.suggestions)
      ? raw.suggestions
      : Array.isArray(raw?.changes)
        ? raw.changes
        : [];

  return items
    .map((item, index): RedlineSuggestion | null => {
      const originalText = String(item?.originalText || item?.original || item?.text || "").trim();
      const suggestedText = String(item?.suggestedText || item?.replacement || item?.revised || item?.revisedText || "").trim();
      if (!originalText || !suggestedText || originalText === suggestedText) {
        return null;
      }
      const found = sourceText.includes(originalText);
      return {
        id: `r-${Date.now()}-${index}`,
        title: String(item?.title || `Suggestion ${index + 1}`).trim(),
        originalText,
        suggestedText,
        reason: String(item?.reason || item?.commentary || "Suggested legal refinement.").trim(),
        status: found ? "pending" : "missing",
      };
    })
    .filter((item): item is RedlineSuggestion => item !== null);
};

export const redlineService = {
  async requestRedlines(chatId: string, sourceText: string, userInstruction?: string): Promise<RedlineSuggestion[]> {
    const trimmed = sourceText.trim();
    if (!trimmed) {
      throw new Error("Select text in Word or open a document with content before requesting redlines.");
    }
    const instruction = userInstruction?.trim()
      ? `Reviewer focus: ${userInstruction.trim()}`
      : "General legal review for clarity, risk, and enforceability.";

    const message = `${REDLINE_PROMPT}\n\n${instruction}\n\nDocument text:\n"""\n${trimmed.slice(0, 12000)}\n"""`;

    const reply = await jurionChatService.sendMessage(chatId, message, {
      metadata: {
        source: "word_addin",
        intent: "redline",
      },
    });

    if (!reply) {
      throw new Error("Empty response from review API.");
    }

    const json = stripFence(reply);
    const parsed = tryParseJson(json);
    if (!parsed) {
      throw new Error("Could not parse redline suggestions. Try again or refine your request.");
    }
    const suggestions = normalize(parsed, trimmed);
    return suggestions;
  },
};
